import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Upload, CheckCircle2, XCircle, AlertCircle,
  FileJson, Image, Clock, ChevronDown,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { facilitiesApi, slideshowApi, importApi, queryKeys, type SingularSlide, type SingularImportResult } from '@/lib/api';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ParsedPreview {
  slides: SingularSlide[];
  totalDuration: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseSingularJson(raw: string): ParsedPreview | string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return 'Invalid JSON — could not parse input.';
  }

  if (!Array.isArray(parsed)) {
    return 'JSON must be an array of slide objects.';
  }
  if (parsed.length === 0) {
    return 'JSON array is empty — nothing to import.';
  }
  if (parsed.length > 200) {
    return `Too many slides (${parsed.length}). Maximum is 200.`;
  }

  const slides: SingularSlide[] = [];
  for (let i = 0; i < parsed.length; i++) {
    const item = parsed[i] as Record<string, unknown>;
    const duration = item['duration'];
    const payload = item['payload'];
    const sponsor = payload && typeof payload === 'object'
      ? (payload as Record<string, unknown>)['Sponsor']
      : undefined;

    if (typeof duration !== 'number' || typeof sponsor !== 'string') {
      return `Slide at index ${i} is missing required fields (duration, payload.Sponsor).`;
    }
    slides.push({
      duration,
      payload: { Sponsor: sponsor },
    });
  }

  const totalDuration = slides.reduce((sum, s) => sum + s.duration, 0);
  return { slides, totalDuration };
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SlidePreviewRow({ slide, index }: { slide: SingularSlide; index: number }) {
  const url = slide.payload.Sponsor.startsWith('//')
    ? `https:${slide.payload.Sponsor}`
    : slide.payload.Sponsor;

  const filename = url.split('/').pop() ?? url;

  return (
    <tr className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
      <td className="py-2 px-3 text-sm text-gray-500 dark:text-gray-400 w-10 text-center">
        {index + 1}
      </td>
      <td className="py-2 px-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-12 bg-gray-100 dark:bg-gray-800 rounded overflow-hidden flex-shrink-0 flex items-center justify-center">
            <img
              src={url}
              alt=""
              className="h-full w-full object-contain"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
                (e.currentTarget.nextElementSibling as HTMLElement | null)?.removeAttribute('hidden');
              }}
            />
            <Image className="h-4 w-4 text-gray-400 hidden" aria-hidden />
          </div>
          <span
            className="text-xs text-gray-600 dark:text-gray-400 truncate max-w-xs"
            title={filename}
          >
            {filename}
          </span>
        </div>
      </td>
      <td className="py-2 px-3 text-sm text-center">
        <span className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-300">
          <Clock className="h-3 w-3 text-gray-400" />
          {slide.duration}s
        </span>
      </td>
    </tr>
  );
}

function ResultPanel({ result }: { result: SingularImportResult }) {
  const allOk = result.summary.failed === 0;
  const allFailed = result.summary.succeeded === 0;

  return (
    <div className={`rounded-lg border p-4 ${
      allFailed
        ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
        : allOk
          ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
          : 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20'
    }`}>
      <div className="flex items-start gap-3">
        {allFailed
          ? <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          : allOk
            ? <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
            : <AlertCircle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
        }
        <div className="flex-1 min-w-0">
          <p className={`font-medium ${
            allFailed ? 'text-red-800 dark:text-red-200'
              : allOk ? 'text-green-800 dark:text-green-200'
                : 'text-yellow-800 dark:text-yellow-200'
          }`}>
            {allFailed
              ? 'Import failed — all images could not be processed'
              : allOk
                ? `Import complete — ${result.summary.succeeded} image${result.summary.succeeded !== 1 ? 's' : ''} imported`
                : `Partial import — ${result.summary.succeeded} of ${result.summary.total} images imported`
            }
          </p>

          {!allFailed && (
            <p className="text-sm mt-1 text-gray-600 dark:text-gray-400">
              Slideshow <strong className="font-mono">#{result.slideshowId}</strong> created for{' '}
              <strong>{result.facilityName}</strong>.{' '}
              You can manage it from the facility's Slideshows page.
            </p>
          )}

          {result.failed.length > 0 && (
            <details className="mt-3">
              <summary className="text-sm font-medium cursor-pointer select-none text-gray-700 dark:text-gray-300 flex items-center gap-1">
                <ChevronDown className="h-3.5 w-3.5" />
                {result.failed.length} failed image{result.failed.length !== 1 ? 's' : ''}
              </summary>
              <ul className="mt-2 space-y-1">
                {result.failed.map((f) => (
                  <li key={f.sortOrder} className="text-xs text-red-700 dark:text-red-300">
                    <span className="font-mono text-gray-500 mr-1">#{f.sortOrder + 1}</span>
                    {f.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AdminImportTab() {
  const [facilityId, setFacilityId] = useState('');
  const [slideshowName, setSlideshowName] = useState('Sponsors');
  const [isDefault, setIsDefault] = useState(false);
  const [rawJson, setRawJson] = useState('');
  const [preview, setPreview] = useState<ParsedPreview | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<SingularImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const { data: facilitiesData } = useQuery({
    queryKey: queryKeys.facilities.list(),
    queryFn: () => facilitiesApi.list({ limit: 200 }),
  });
  const facilities = (facilitiesData?.facilities ?? [])
    .filter(f => f.isActive)
    .sort((a, b) => a.name.localeCompare(b.name));

  // Fetch existing slideshows for the selected facility
  const { data: existingSlideshowsData } = useQuery({
    queryKey: ['admin-import-slideshows', facilityId],
    queryFn: () => slideshowApi.list({ facilityId }),
    enabled: !!facilityId,
  });
  const existingSlideshows = existingSlideshowsData?.slideshows ?? [];
  const nameConflict = !!slideshowName.trim() &&
    existingSlideshows.some(s => s.name.toLowerCase() === slideshowName.trim().toLowerCase());

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleParse = () => {
    setParseError(null);
    setPreview(null);
    setResult(null);
    setImportError(null);

    const outcome = parseSingularJson(rawJson.trim());
    if (typeof outcome === 'string') {
      setParseError(outcome);
    } else {
      setPreview(outcome);
    }
  };

  const handleImport = async () => {
    if (!preview || !facilityId || !slideshowName.trim()) return;

    setImporting(true);
    setResult(null);
    setImportError(null);

    try {
      const res = await importApi.importSingular({
        facilityId,
        name: slideshowName.trim(),
        isDefault,
        json: preview.slides,
      });
      setResult(res);
      // Bust the slideshows cache so SlideshowsPage picks up the new entry immediately
      if (res.summary.succeeded > 0) {
        queryClient.invalidateQueries({ queryKey: ['slideshows'] });
        queryClient.invalidateQueries({ queryKey: ['admin-import-slideshows', facilityId] });
        setRawJson('');
        setPreview(null);
      }
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setPreview(null);
    setParseError(null);
    setResult(null);
    setImportError(null);
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  const canImport = !!preview && !!facilityId && !!slideshowName.trim() && !importing && !nameConflict;

  return (
    <div className="max-w-3xl space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <FileJson className="h-5 w-5 text-blue-500" />
          Import Singular.live Script
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Paste a Singular.live ad-script JSON array to create a new slideshow for a facility.
          Each image is downloaded and stored in CSM's own S3 bucket.
        </p>
      </div>

      {/* Result panel (shown after import) */}
      {result && <ResultPanel result={result} />}

      {/* Step 1: Facility + name */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
          1 — Target Facility
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Facility
            </label>
            <select
              value={facilityId}
              onChange={(e) => setFacilityId(e.target.value)}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select facility…</option>
              {facilities.map((f) => (
                <option key={f.facilityId} value={f.facilityId}>
                  {f.name} ({f.facilityId})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Slideshow Name
            </label>
            <input
              type="text"
              value={slideshowName}
              onChange={(e) => setSlideshowName(e.target.value)}
              placeholder="e.g. Sponsors"
              className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ${
                nameConflict
                  ? 'border-red-400 dark:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
              }`}
            />
            {nameConflict && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                <XCircle className="h-3.5 w-3.5 flex-shrink-0" />
                A slideshow with this name already exists for this facility.
              </p>
            )}
          </div>
        </div>

        {/* Existing slideshows for the selected facility */}
        {facilityId && existingSlideshows.length > 0 && (
          <div className="rounded-md border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 px-3 py-2">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
              Existing slideshows for this facility:
            </p>
            <ul className="space-y-0.5">
              {existingSlideshows.map(s => (
                <li key={s.id} className="flex items-center gap-2 text-xs">
                  <span className={`font-medium ${
                    s.name.toLowerCase() === slideshowName.trim().toLowerCase()
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    {s.name}
                  </span>
                  {s.isDefault && (
                    <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">default</Badge>
                  )}
                  <span className="text-gray-400">
                    {s._count?.images ?? 0} image{(s._count?.images ?? 0) !== 1 ? 's' : ''}
                  </span>
                  {s.name.toLowerCase() === slideshowName.trim().toLowerCase() && (
                    <span className="text-red-500 font-medium">← name conflict</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {facilityId && existingSlideshows.length === 0 && (
          <p className="text-xs text-gray-400 dark:text-gray-500">
            No existing slideshows for this facility.
          </p>
        )}

        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Set as default slideshow for this facility
          </span>
        </label>
      </div>

      {/* Step 2: JSON input */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-3">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
          2 — Paste Singular.live JSON
        </h3>

        <p className="text-xs text-gray-500 dark:text-gray-400">
          Expected format:{' '}
          <code className="bg-gray-100 dark:bg-gray-800 rounded px-1 py-0.5">
            {'[{"duration":8,"payload":{"Sponsor":"https://..."}}]'}
          </code>
        </p>

        <textarea
          value={rawJson}
          onChange={(e) => {
            setRawJson(e.target.value);
            if (preview || parseError) handleReset();
          }}
          rows={10}
          spellCheck={false}
          placeholder={'[\n  {\n    "duration": 8,\n    "payload": { "Sponsor": "https://..." }\n  }\n]'}
          className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 text-gray-900 dark:text-gray-100 px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
        />

        {parseError && (
          <div className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400">
            <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            {parseError}
          </div>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={handleParse}
          disabled={!rawJson.trim()}
        >
          Parse &amp; Preview
        </Button>
      </div>

      {/* Step 3: Preview */}
      {preview && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
              3 — Preview
            </h3>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{preview.slides.length} slides</Badge>
              <Badge variant="outline">
                <Clock className="h-3 w-3 mr-1" />
                {formatDuration(preview.totalDuration)} total
              </Badge>
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto rounded-md border border-gray-100 dark:border-gray-800">
            <table className="w-full text-left">
              <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400 uppercase">
                <tr>
                  <th className="py-2 px-3 w-10 text-center">#</th>
                  <th className="py-2 px-3">Image</th>
                  <th className="py-2 px-3 text-center">Duration</th>
                </tr>
              </thead>
              <tbody>
                {preview.slides.map((slide, i) => (
                  <SlidePreviewRow key={i} slide={slide} index={i} />
                ))}
              </tbody>
            </table>
          </div>

          {importError && (
            <div className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400">
              <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              {importError}
            </div>
          )}

          <div className="flex items-center gap-3 pt-1">
            <Button
              onClick={handleImport}
              disabled={!canImport}
              className="flex items-center gap-2"
            >
              <Upload className="h-4 w-4" />
              {importing
                ? 'Importing…'
                : `Import ${preview.slides.length} image${preview.slides.length !== 1 ? 's' : ''}`}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleReset} disabled={importing}>
              Clear
            </Button>
            {!facilityId && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Select a facility above before importing.
              </p>
            )}
            {nameConflict && (
              <p className="text-xs text-red-600 dark:text-red-400">
                Rename the slideshow to resolve the conflict.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
